import { Tooltip, Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { AppContext } from 'jsx';
import React, { useContext, useState } from 'react';
import { Info } from 'react-feather';
import { useTranslation } from 'react-i18next';
import {
  PermissionSetOption,
  PermissionSetOptionGroups,
  PermissionSetRow,
  UserPermissionsAPI,
} from '../util/permissionsForEdit';
import { Cell, Row } from './Table';

export const CHECKBOX_COLUMN_WIDTH = 200;

type Props = {
  perSet: PermissionSetRow;
  userPermissions: UserPermissionsAPI[];
  disabled?: boolean;
  handleChange: (
    e: CheckboxChangeEvent,
    userPermission: UserPermissionsAPI | undefined,
    userPermissions: UserPermissionsAPI[],
    permissionId: number,
    permissionObject: PermissionSetOption
  ) => void;
};
export default function ObjectPermissionRow({
  perSet,
  userPermissions,
  disabled,
  handleChange,
}: Props) {
  const { allPermissionsMap } = useContext(AppContext);
  const [t] = useTranslation(['authorization', 'common']);

  const permissionCodenames = userPermissions.map(
    userPerm => userPerm.codename
  );
  const [checkedList, setCheckedList] =
    useState<(string | undefined)[]>(permissionCodenames);

  let optionsWithViewPermission: PermissionSetOption[] = [];
  perSet.options.forEach(option => {
    const found = checkedList.find(item => item === option.codename);
    if (found && option.view) {
      optionsWithViewPermission.push(option);
    }
  });

  return (
    <Row>
      <Cell style={{ paddingLeft: 16 }}>{perSet.label}</Cell>
      {perSet.options.map((option: PermissionSetOption, i: number) => {
        if (!option?.codename) {
          return <Cell key={`empty${i}`} width={CHECKBOX_COLUMN_WIDTH}></Cell>;
        }
        const permission = allPermissionsMap.get(option.codename);

        const userPermission = userPermissions.find(
          (x: any) =>
            x.codename === option.codename &&
            (!x.object_id || x.object_id === perSet.object_id)
        );

        const isInheritedFromGroup = userPermission
          ? userPermission.groups?.length > 0
          : false;
        const isInherited = isInheritedFromGroup || option.isInherited === true;

        const isChecked =
          perSet.object_id &&
          userPermissions
            .filter(
              x => x.object_id?.toString() === perSet.object_id?.toString()
            )
            .map(x => x.codename)
            .includes(option.codename);

        const isViewDisabled =
          optionsWithViewPermission.find(opt => opt.view === option.codename) &&
          checkedList.includes(option.codename);

        return (
          <Cell
            key={option.codename}
            width={CHECKBOX_COLUMN_WIDTH}
            style={{ justifyContent: 'center' }}
          >
            <div style={{ position: 'relative' }}>
              <Checkbox
                data-ta={option.codename}
                checked={!!isChecked || option.isInherited}
                disabled={
                  isInherited || disabled || option.disabled || isViewDisabled
                }
                key={option.codename}
                onChange={e => {
                  if (e.target.checked) {
                    setCheckedList(
                      option.view
                        ? [
                            ...checkedList,
                            option.codename,
                            ...(Array.isArray(option.view)
                              ? option.view
                              : [option.view]),
                          ]
                        : [...checkedList, option.codename]
                    );
                  } else {
                    const filteredCheckList = checkedList.filter(
                      item => item !== option.codename
                    );
                    setCheckedList(filteredCheckList);
                  }
                  if (permission)
                    handleChange(
                      e,
                      userPermission,
                      userPermissions,
                      permission,
                      option
                    );
                }}
              />
              {isInherited && (
                <div style={{ position: 'absolute', left: 24, top: 0 }}>
                  <Tooltip
                    title={
                      isInheritedFromGroup ? (
                        <span style={{ fontSize: 12 }}>
                          {t('This permission is inherited from the')}{' '}
                          {userPermission?.groups.length === 1
                            ? t('group')
                            : t('groups')}{' '}
                          {userPermission?.groups
                            .map(
                              (a: PermissionSetOptionGroups, index: number) =>
                                index === userPermission?.groups.length - 1 &&
                                index > 0
                                  ? t("and '(NAME)'", { groupName: a.name })
                                  : `'${a.name}'`
                            )
                            .join(', ')
                            .replace(', and', ' and')}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, lineHeight: 1 }}>
                          {t(
                            "authorization:This permission is inherited from this user's global permissions"
                          )}
                        </span>
                      )
                    }
                  >
                    <Info size={15} className="text-gray-400" />
                  </Tooltip>
                </div>
              )}
            </div>
          </Cell>
        );
      })}
    </Row>
  );
}
